/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { reduxForm, getFormSyncErrors, FormProps, change, getFormValues, reset } from 'redux-form';
import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  Icon,
  Modal,
  Button,
  Popover,
  ActionList,
  SkeletonDisplayText,
  Spinner
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import * as sass from 'sass';
import { v4 } from 'uuid';
import { MobileCancelMajor, ChevronDownMinor } from '@shopify/polaris-icons';

import { CodeEditorComponent } from './code-editor-component';
import {
  templateRender
  // templateBuilder,
  // formatTemplate
} from 'boost-template-engine';
import { combineWidgets, calculateDocSize } from 'src/helpers/ssr-helper';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { DEVICE } from 'src/constants/theme-settings';
import { getShop } from 'src/helpers';
import EditCode from 'src/assets/images/shopify-integration-settings/edit-file.png';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { KEY_EDITOR_STATE, MAPPING_AVAILABLE_TEMPLATE } from 'src/constants/shopify-integration';
// import { URL } from 'src/constants/links';
import * as ShopifyIntegrationSettingsAction from 'src/stores/actions/shopify-integration/shopify-integration-settings-actions';
import * as TemplateInstanceAction from 'actions/shopify-integration/template-instance-actions';
import { trackFSEvent_ChangeCodeEditor } from 'src/integrations/fullStory';

type PropsFromState = {
  instantWidget: anu,
  widgetSettings: any,
  scssContent: string
};

type PropsFromDispatch = {};

type ComposedProps = PropsFromState & PropsFromDispatch & FormProps & WithI18nProps;

const WIDGET_NAME_CUSTOMIZATION_JS = 'Custom javascript';
const WIDGET_NAME_CUSTOMIZATION_CSS = 'Custom css';

class CodeEditor extends Component<ComposedProps, State> {
  state = {
    isShowModalReset: false,
    isShowVersionOptions: false,
    isLoadingVersionContent: false,
    savingWidget: null
  };
  initializedPreview = false;
  compiledSettingsCss = '';
  originalCompiledSizes = 0;
  originalRenderedSizes = 0;

  componentDidMount() {
    const {
      widgetSettings,
      scssContent,
      instantWidget,
      selectedPage,
      updateField,
      customizeTemplate
    } = this.props;

    // first time compile scss from scss content and widget settings
    if (
      !_.isEmpty(widgetSettings) &&
      !_.isEmpty(scssContent) &&
      _.isEmpty(this.compiledSettingsCss)
    ) {
      this.compileScss(widgetSettings, scssContent);
    }

    // get selected widget from instance template
    if (
      !_.isEmpty(instantWidget) &&
      !_.isEmpty(instantWidget.widgets) &&
      _.isEmpty(this.selectedWidget)
    ) {
      const mainWidgetId = _.get(
        instantWidget,
        `widgetIds.${[MAPPING_AVAILABLE_TEMPLATE[selectedPage?.value]]}`
      );

      const selectedWidget =
        instantWidget.widgets[mainWidgetId] || Object.values(instantWidget.widgets)[0];

      this.selectedWidget = selectedWidget;
    }

    updateField('customizeTemplate', customizeTemplate);
    Emitter.on(EVENT_KEY.SAVE_EDITOR_STATE, this.handleSaveEditorState);
  }

  componentWillReceiveProps(nextProps) {
    const { customizeTemplate, updateTemplateInstancesLoading } = this.props;
    const {
      customizeTemplate: nextCustomizeTemplate,
      codeEditTemplate,
      editorModel,
      updateField,
      updateTemplateInstancesLoading: nextUpdateTemplateInstancesLoading
    } = nextProps;
    if (
      customizeTemplate !== nextCustomizeTemplate &&
      codeEditTemplate?.widgetId &&
      this.editorRef &&
      this.monacoRef
    ) {
      let model = editorModel[codeEditTemplate?.widgetId];
      if (!model) {
        let language = 'liquid';
        if (codeEditTemplate?.widgetName === WIDGET_NAME_CUSTOMIZATION_JS) {
          language = 'javascript';
        } else if (codeEditTemplate?.widgetName === WIDGET_NAME_CUSTOMIZATION_CSS) {
          language = 'css';
        }
        model = this.monacoRef.editor.createModel(nextCustomizeTemplate, language);
        this.handleSaveEditorModel(model);
      }
      this.editorRef.setModel(model);

      const editorState = this.getEditorStateFromLocalStorage();
      this.editorRef.restoreViewState(editorState[codeEditTemplate?.widgetId] || {});
      this.editorRef.focus();
      updateField('customizeTemplate', nextCustomizeTemplate);

      this.currentWidget = codeEditTemplate;
    }
    if (updateTemplateInstancesLoading && !nextUpdateTemplateInstancesLoading) {
      this.forceUpdate();
    }
  }

  componentWillUnmount() {
    Emitter.off(EVENT_KEY.SAVE_EDITOR_STATE, this.handleSaveEditorState);
  }

  getAllWidgets = props => {
    const { formValues } = props || this.props;
    const instantWidgetFormValues = _.get(formValues, 'instantWidget', {});
    const widgets = _.get(instantWidgetFormValues, 'widgets', {});

    const allWidgets = Object.values(widgets).reduce((cur, next) => {
      return [...cur, ...next];
    }, []);
    return allWidgets;
  };

  getEditorStateFromLocalStorage = () => {
    try {
      let editorState = window.localStorage.getItem(`${getShop()}-${KEY_EDITOR_STATE}`) || '{}';
      editorState = JSON.parse(editorState);
      return editorState;
    } catch (e) {
      console.log('Error', e);
      return {};
    }
  };

  handleSaveEditorState = () => {
    if (!this.editorRef) {
      return;
    }

    try {
      const { codeEditTemplate } = this.props;
      const editorState = this.getEditorStateFromLocalStorage();
      window.localStorage.setItem(
        `${getShop()}-${KEY_EDITOR_STATE}`,
        JSON.stringify({
          ...editorState,
          [codeEditTemplate?.widgetId]: this.editorRef.saveViewState()
        })
      );
      this.setState({
        isShowVersionOptions: false
      });
    } catch (e) {
      console.log('Error', e);
    }
  };

  handleEditorDidMount = (editor, monaco) => {
    const { codeEditTemplate } = this.props;
    // here is the editor instance
    this.editorRef = editor;
    this.monacoRef = monaco;
    this.props.onUpdateModel(this.editorRef.getModel());
    if (codeEditTemplate?.widgetName === WIDGET_NAME_CUSTOMIZATION_JS) {
      this.monacoRef.editor.setModelLanguage(this.editorRef.getModel(), 'javascript');
    } else if (codeEditTemplate?.widgetName === WIDGET_NAME_CUSTOMIZATION_CSS) {
      this.monacoRef.editor.setModelLanguage(this.editorRef.getModel(), 'css');
    }
  };

  handleSaveEditorModel = model => {
    this.props.onUpdateModel(model);
  };

  compileScss = async (widgetSettings, scssContent) => {
    if (!_.isEmpty(widgetSettings) && !_.isEmpty(scssContent)) {
      const sassMapString = `$settings: (${this.drill(widgetSettings)});\n`;
      const compiledScss = sassMapString + '\n' + scssContent;
      const { css } = sass.compileString(compiledScss);

      this.compiledSettingsCss = css;
      this.compileMainTemplate();
    }
  };

  getPreviewData = async props => {
    const { previewData, updateFieldReducer } = props || this.props;
    if (_.isEmpty(previewData)) {
      const host = process.env.REACT_APP_FILTER_URL || 'https://staging.bc-solutions.net';
      const filterUrl = host + '/bc-sf-filter/filter';
      const params = this.getRequestParams();
      const response = await fetch(`${filterUrl}?${new URLSearchParams(params)}`, {
        method: 'GET'
      });
      const previewData = await response.json();
      updateFieldReducer('previewData', previewData);
    }
    this.compileMainTemplate();
  };

  getRequestParams = props => {
    const { selectedPage, device, formValues } = props || this.props;
    const limitPerPage = _.get(formValues, `setting.${MENU_ID.PRODUCT_LIST}.productsPerPage`, 16);
    const productsPerRowOnDesktop = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_LIST}.productsPerRowOnDesktop`,
      3
    );
    const productsPerRowOnMobile = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_LIST}.productsPerRowOnMobile`,
      2
    );

    const data = {
      t: Date.now(),
      sid: v4(),
      _: 'pf',
      shop: getShop(),
      page: 1,
      sort: 'Relevance',
      locale: 'en',
      event_type: 'init',
      build_filter_tree: 'true',
      handle: 'all',
      previewMode: true,
      pg: selectedPage?.value,
      limit: limitPerPage,
      viewAs: `grid--${
        device === DEVICE.DESKTOP ? productsPerRowOnDesktop : productsPerRowOnMobile
      }`
    };

    if (device === DEVICE.MOBILE) {
      data.device = 'mobile, tabletPortraitMax';
    }
    return data;
  };

  /**
   * compile & render preview template
   */
  compileMainTemplate = async () => {
    const { widgetSettings, previewData, instantWidget } = this.props;
    const allWidgets = this.getAllWidgets();

    if (
      !_.isEmpty(this.selectedWidget) &&
      !_.isEmpty(allWidgets) &&
      !_.isEmpty(widgetSettings) &&
      !_.isEmpty(this.compiledSettingsCss) &&
      !_.isEmpty(previewData)
    ) {
      const compiledTemplate = await combineWidgets(
        this.selectedWidget[0]?.widgetName,
        allWidgets,
        widgetSettings,
        this.compiledSettingsCss,
        instantWidget
      );
      // console.log('compiledTemplate', compiledTemplate); originalRenderedSizes
      const renderedTemplate = templateRender(compiledTemplate, {
        ...previewData,
        request: this.getRequestParams()
      });

      // set origin render size
      if (!this.originalRenderedSizes || this.originalRenderedSizes === 0) {
        this.originalRenderedSizes = calculateDocSize(renderedTemplate);
      }
      // set origin doc size
      if (!this.originalCompiledSizes || this.originalCompiledSizes === 0) {
        this.originalCompiledSizes = calculateDocSize(compiledTemplate);
      }
    }
  };

  drill = obj => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const buildValue = value => {
        if (!value) return `'${key}': inherit,`;
        if (value && typeof value === 'object') return `'${key}': (${this.drill(value)}),`;
        return `'${key}': ${JSON.stringify(String(value))},`;
      };
      acc += buildValue(value);
      return acc;
    }, '');
  };

  handleShowModalReset = () => {
    const { codeEditTemplate, templatesEditing } = this.props;

    if (
      templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate !==
      templatesEditing[codeEditTemplate?.widgetId]?.customizeTemplate
    ) {
      this.setState({
        isShowModalReset: true
      });
    } else {
      // If code is not different from original, just turn off toggle
      this.updateWidget('hasCustomized', false);
    }
  };

  handleHideModalReset = () => {
    this.discardWidgetId = undefined;
    this.setState({
      isShowModalReset: false
    });
  };

  handleSelectSection = widgetId => {
    const {
      updateMultiFieldsReducer,
      templatesEditing,
      instantWidget,
      updateTemplateInstancesLoading
    } = this.props;
    const flattenWidgets = _.flattenDeep(Object.values(instantWidget.widgets));
    const template =
      updateTemplateInstancesLoading && this.savingWidget?.widgetId === widgetId
        ? { ...templatesEditing[widgetId], widgetId }
        : _.cloneDeep(flattenWidgets.find(w => w.widgetId === widgetId));
    this.handleSaveEditorState();

    const customizeTemplate = !_.isEmpty(templatesEditing[widgetId]?.customizeTemplate)
      ? templatesEditing[widgetId]?.customizeTemplate
      : template.widgetTemplate;

    const payload = {
      customizeTemplate,
      codeEditTemplate: template
    };

    Emitter.emit(EVENT_KEY.SELECT_NODE_TREE_CODE_EDITOR, template.widgetName);
    updateMultiFieldsReducer(payload);
  };

  onChangeCode = async value => {
    // const {
    //   widgetSettings,
    //   previewData,
    //   updateFieldReducer,
    //   templatesEditing,
    //   codeEditTemplate
    // } = this.props;
    // const verifyCode = async value => {
    //   try {
    //     const detectLoop = detectLoops(value);
    //     console.log('detectLoop', detectLoop);
    //     const memoryBefore = window.performance?.memory?.usedJSHeapSize || 0;
    //     console.time('verifyCodeCustomization');

    //     // Start calculate performance usage for compile and render customizations
    //     await waitFor(200); // wait for fresh memory usage by react and another script
    //     const startCompileTime = new Date();
    //     // calculate compile template
    //     const compiledTemplate = await combineWidgets(
    //       'Filter tree and Product listing',
    //       this.selectedWidget,
    //       widgetSettings,
    //       this.compiledSettingsCss
    //     );
    //     const memoryAfterCompile = window.performance?.memory?.usedJSHeapSize || 0;
    //     const endCompileTime = new Date();
    //     const verifyRendered = templateRender(compiledTemplate, {
    //       ...previewData,
    //       request: this.getRequestParams()
    //     });
    //     const compiledSize = calculateDocSize(compiledTemplate);
    //     const customizationRenderedSize = calculateDocSize(verifyRendered);

    //     // log customization calculations info
    //     const endRenderTime = new Date();
    //     const timeCompileDiff = endCompileTime - startCompileTime;
    //     const timeRenderDiff = endRenderTime - endCompileTime;

    //     const memoryAfterRender = window.performance?.memory?.usedJSHeapSize || 0;

    //     const memoryCompileDiff = memoryAfterCompile - memoryBefore;
    //     const memoryRenderDiff = memoryAfterRender - memoryAfterCompile;

    //     console.log(`Template size:
    //       - Original compile size: ${bytesToMB(this.originalCompiledSizes)} MB
    //       - Customization compile size: ${bytesToMB(compiledSize)} MB
    //       - Original render size: ${bytesToMB(this.originalRenderedSizes)} MB
    //       - Customization size ${bytesToMB(customizationRenderedSize)} MB`);

    //     console.log(`Customization Template Memory
    //     - Before compile and render: ${memoryBefore} bytes,
    //     - After compile:  ${memoryAfterCompile} bytes
    //     - After render:  ${memoryAfterRender} bytes`);
    //     console.log(`Memory used:
    //       - Compile ${bytesToMB(memoryCompileDiff)} MB
    //       - Render ${bytesToMB(memoryRenderDiff)} MB`);
    //     console.log(`Time compile and render customization:
    //       - Compile: ${timeCompileDiff.toFixed(2)} milliseconds
    //       - Render: ${timeRenderDiff.toFixed(2)} milliseconds
    //       `);
    //     console.timeEnd('verifyCodeCustomization');
    //     // console.log(`template builded: ${verifyBuilded}`);
    //     // console.log(`template rendered: ${verifyRendered}`);
    //     return true;
    //   } catch (e) {
    //     console.log('Verify code failed ---------> ', e);
    //     return false;
    //   }
    // };

    // const compiledTemplate = await verifyCode(value);

    // if (compiledTemplate) {
    //   this.updateWidget('customizeTemplate', value);
    //   updateFieldReducer('templatesEditing', {
    //     ...templatesEditing,
    //     [codeEditTemplate?.widgetId]: {
    //       customizeTemplate: value,
    //       originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
    //       isEditing: !_.isEqual(
    //         templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
    //         value
    //       )
    //     }
    //   });
    //   this.compileMainTemplate();
    // } else {
    //   updateFieldReducer(
    //     'customizeTemplate',
    //     templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate
    //   );
    //   this.editorRef.trigger('Invalid template', 'undo');
    // }

    this.updateWidget('customizeTemplate', value);
    // this.compileMainTemplate();
  };

  updateWidget = (field, value) => {
    const { codeEditTemplate, instantWidget, templatesEditing, updateFieldReducer, updateField } =
      this.props;
    const payloadTemplatesEditing = _.cloneDeep(templatesEditing);
    // codeEditTemplate?.widgetId for the parent element

    const rootKeys = [];
    const widgets = _.get(instantWidget, 'widgets', {});
    Object.keys(widgets).forEach(key => {
      const arrWidget = widgets[key];
      const isExistSharingWidget = arrWidget.find(
        w => w?.widgetName === codeEditTemplate?.widgetName
      );
      if (isExistSharingWidget) {
        rootKeys.push(key);
      }
    });

    if (!_.isEmpty(rootKeys)) {
      rootKeys.forEach(rootKey => {
        this.updateWidgetDetail(field, value, rootKey, payloadTemplatesEditing);
      });
    } else {
      const refId = codeEditTemplate?.widgetId;
      this.updateWidgetDetail(field, value, refId, payloadTemplatesEditing);
    }

    if (field === 'customizeTemplate') {
      updateField('customizeTemplate', value);
      updateFieldReducer('templatesEditing', {
        ...payloadTemplatesEditing,
        [codeEditTemplate?.widgetId]: {
          ...templatesEditing[codeEditTemplate?.widgetId],
          // originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
          customizeTemplate: value,
          isEditing: !_.isEqual(
            templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
            value
          )
        }
      });
    }
  };

  updateWidgetDetail = (field, value, rootKey, payloadTemplatesEditing) => {
    const {
      codeEditTemplate,
      instantWidget,
      templatesEditing,
      updateFieldTemplateInstancesWidgetsReducer
    } = this.props;
    // codeEditTemplate?.widgetId for the parent element
    // const instantWidgetFormValues = _.get(formValues, 'instantWidget', {});

    const clonedInstantWidget = _.cloneDeep(instantWidget);
    const refWidget = _.get(clonedInstantWidget, `widgets.${rootKey}`, []);
    refWidget.forEach(widget => {
      if (widget?.widgetName === codeEditTemplate?.widgetName) {
        _.set(widget, field, value);

        if (field === 'customizeTemplate') {
          // edit other sharing widget code
          payloadTemplatesEditing[widget?.widgetId] = {
            ...payloadTemplatesEditing[widget?.widgetId],
            customizeTemplate: value,
            // originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
            isEditing: !_.isEqual(
              templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
              value
            )
          };
        }
      }
    });

    updateFieldTemplateInstancesWidgetsReducer('templateInstancesPreview', clonedInstantWidget);
  };

  handleConfirmClose = widgetId => {
    const { templatesEditing } = this.props;
    this.discardWidgetId = widgetId;
    if (templatesEditing[widgetId]?.isEditing) {
      this.setState({ isShowModalReset: true });
      return;
    }
    this.handleDiscardChange();
  };

  handleDiscardChange = () => {
    const {
      templatesEditing,
      onDeleteModel,
      instantWidget,
      updateMultiFieldsReducer,
      codeEditTemplate,
      updateFieldReducer
    } = this.props;

    const newTemplatesEditing = _.cloneDeep(templatesEditing);
    // Delete object widget with latest customizeTemplate
    delete newTemplatesEditing[this.discardWidgetId];

    if (codeEditTemplate?.widgetId === this.discardWidgetId) {
      let currentIndexFile = _.findIndex(
        Object.keys(templatesEditing),
        widgetId => widgetId === this.discardWidgetId
      );
      if (
        currentIndexFile > 0 ||
        (currentIndexFile === 0 && Object.keys(templatesEditing).length > 1)
      ) {
        currentIndexFile = currentIndexFile > 0 ? currentIndexFile - 1 : currentIndexFile;
        const newWidgetId = Object.keys(newTemplatesEditing)[currentIndexFile];

        const flattenWidgets = _.flattenDeep(Object.values(instantWidget.widgets));
        const template = _.cloneDeep(flattenWidgets.find(w => w.widgetId === newWidgetId));
        const customizeTemplate = !_.isEmpty(newTemplatesEditing[newWidgetId]?.customizeTemplate)
          ? newTemplatesEditing[newWidgetId]?.customizeTemplate
          : template.widgetTemplate || template.widgetTemplate;

        const payload = {
          customizeTemplate,
          codeEditTemplate: template,
          templatesEditing: newTemplatesEditing
        };

        Emitter.emit(EVENT_KEY.SELECT_NODE_TREE_CODE_EDITOR, template.widgetName);
        updateMultiFieldsReducer(payload);
      } else {
        const payload = {
          customizeTemplate: '',
          codeEditTemplate: {},
          templatesEditing: {}
        };

        Emitter.emit(EVENT_KEY.SELECT_NODE_TREE_CODE_EDITOR, null);
        updateMultiFieldsReducer(payload);
      }
    } else {
      updateFieldReducer('templatesEditing', newTemplatesEditing);
    }

    // Delete model of discarded widget id
    onDeleteModel(this.discardWidgetId);
    if (this.state.isShowModalReset) {
      this.setState({ isShowModalReset: false });
    }
    this.discardWidgetId = undefined;
  };

  handleSave = async () => {
    try {
      const {
        templateId,
        templateName,
        codeEditTemplateFormValues,
        templatesEditing,
        updateTemplateInstancesWidgets,
        updateMultiFieldsReducer,
        getWidgetVersions
      } = this.props;
      this.savingWidget = codeEditTemplateFormValues;
      this.setState({
        savingWidget: codeEditTemplateFormValues
      });

      const codeEditTemplatePayload = _.cloneDeep(codeEditTemplateFormValues);
      codeEditTemplatePayload.hasCustomized = !_.isEqual(
        codeEditTemplatePayload?.widgetTemplate,
        codeEditTemplatePayload?.customizeTemplate
      );

      await updateTemplateInstancesWidgets(templateId, [codeEditTemplatePayload]);

      trackFSEvent_ChangeCodeEditor(templateName, templateId, codeEditTemplatePayload);

      const payload = {
        templatesEditing: {
          ...templatesEditing,
          [codeEditTemplateFormValues?.widgetId]: {
            ...templatesEditing[codeEditTemplateFormValues?.widgetId],
            originalTemplate: codeEditTemplateFormValues?.customizeTemplate,
            isEditing: false
          }
        }
      };

      // If user viewing other file while saving, after saving success -> compare to decide which file to show
      if (
        !this.currentWidget ||
        this.currentWidget?.widgetId === codeEditTemplateFormValues?.widgetId
      ) {
        payload.codeEditTemplate = codeEditTemplateFormValues;
      } else {
        payload.codeEditTemplate = this.currentWidget;
      }

      updateMultiFieldsReducer(payload);
      getWidgetVersions(templateId, codeEditTemplateFormValues?.widgetId);
    } catch (e) {
      console.log('ERROR -------> ', e);
    }
  };

  toggleVersionOptions = () => {
    this.setState({
      isShowVersionOptions: !this.state.isShowVersionOptions
    });
  };

  isEmptyVersion = () => {
    const { codeEditTemplate, widgetVersion } = this.props;
    const versions = _.get(widgetVersion, `${codeEditTemplate?.widgetId}.versions`, []);

    if (_.isEmpty(versions) || versions?.length === 1) {
      return true;
    }
    return false;
  };

  getVersionText = (version, index) => {
    if (!version) {
      return '';
    }

    const { i18n, widgetVersion, codeEditTemplate } = this.props;
    const versions = _.get(widgetVersion, `${codeEditTemplate?.widgetId}.versions`, []);
    const length = versions.length;

    const now = moment();
    const lastModified = moment(version.lastModified);
    let text = '';
    if (now.diff(lastModified, 'days') <= 7) {
      if (index === 0) {
        text = `${i18n.translate(
          'AdminHTML.ShopifyIntegration.CodeEditor.current'
        )} - ${lastModified.fromNow()}`;
      } else if (index === versions.length - 1) {
        text = `${i18n.translate(
          'AdminHTML.ShopifyIntegration.CodeEditor.original'
        )} - ${lastModified.fromNow()}`;
      } else {
        text = `${length - index} - ${lastModified.fromNow()}`;
      }
      return text;
    }

    if (index === 0) {
      text = `${i18n.translate('AdminHTML.ShopifyIntegration.CodeEditor.current')} - ${text}`;
    } else if (index === versions.length - 1) {
      text = `${i18n.translate('AdminHTML.ShopifyIntegration.CodeEditor.original')} - ${text}`;
    } else {
      text = `${length - index} - ${lastModified.format('MMM DD')} at ${lastModified.format(
        'h:mm a'
      )}`;
    }

    return text;
  };

  selectVersionCode = async versionId => {
    const {
      updateFieldTemplateInstancesWidgetsReducer,
      getWidgetVersionsContent,
      widgetVersion,
      codeEditTemplate
    } = this.props;
    const newWidgetVersion = _.cloneDeep(widgetVersion);
    _.set(newWidgetVersion, `${codeEditTemplate?.widgetId}.selectedVersionId`, versionId);

    this.setState({
      isShowVersionOptions: false
    });

    updateFieldTemplateInstancesWidgetsReducer('widgetVersion', newWidgetVersion);

    try {
      this.setState({
        isLoadingVersionContent: true
      });

      const result = await getWidgetVersionsContent(
        codeEditTemplate?.templateId,
        codeEditTemplate?.widgetId,
        versionId
      );

      const content = _.get(result, 'payload.data');
      if (!_.isNil(content)) {
        this.updateWidget('customizeTemplate', content);
        this.editorRef.setValue(content);
      }
    } catch (error) {
      console.log('ERROR', error);
    } finally {
      this.setState({
        isLoadingVersionContent: false
      });
    }
  };

  render() {
    const {
      i18n,
      isExpandFullscreen,
      activeMenuItem,
      instantWidget: instantWidgetReducer,
      widgetSettings,
      scssContent,
      codeEditTemplate,
      customizeTemplate,
      // dirty,
      pristine,
      submitting,
      handleSubmit,
      templatesEditing,
      updateTemplateInstancesLoading,
      widgetVersion,
      widgetVersionLoading
    } = this.props;
    const { isShowVersionOptions, isLoadingVersionContent, savingWidget } = this.state;

    const previewClass = classNames('visual-editor-code', {
      full: isExpandFullscreen,
      'overflow-hidden':
        activeMenuItem === MENU_ID.QUICK_VIEW || activeMenuItem === MENU_ID.AJAX_CART
    });

    const isEmptyVersion = this.isEmptyVersion();
    const versions = _.get(widgetVersion, `${codeEditTemplate?.widgetId}.versions`, []);
    const selectedVersionId = _.get(
      widgetVersion,
      `${codeEditTemplate?.widgetId}.selectedVersionId`
    );
    const selectedVersionIndex = versions.findIndex(item => item.versionId === selectedVersionId);
    const selectedVersion = versions[selectedVersionIndex];
    const selectedVersionText = this.getVersionText(selectedVersion, selectedVersionIndex);

    if (_.isEmpty(instantWidgetReducer) || _.isEmpty(widgetSettings) || _.isEmpty(scssContent)) {
      return null;
    }

    return (
      <div className={previewClass}>
        {!_.isEmpty(codeEditTemplate) ? (
          <div className="visual-editor-code__container">
            <div className="visual-editor-code__col">
              <div className="visual-editor-code__files">
                {Object.keys(templatesEditing).map(widgetId => (
                  <div
                    key={widgetId}
                    className={classNames('visual-editor-code__file-item', {
                      active: widgetId === codeEditTemplate?.widgetId,
                      editing: templatesEditing[widgetId]?.isEditing
                    })}
                    onClick={() => this.handleSelectSection(widgetId)}>
                    <span>{templatesEditing[widgetId]?.name}</span>
                    <Button
                      onClick={e => {
                        e.stopPropagation();
                        this.handleConfirmClose(widgetId);
                      }}>
                      {templatesEditing[widgetId]?.isEditing && (
                        <span className="changed-spot"></span>
                      )}
                      <Icon source={MobileCancelMajor} />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="visual-editor-code__version">
                {widgetVersionLoading &&
                (!savingWidget?.widgetId ||
                  savingWidget?.widgetId === codeEditTemplate?.widgetId) ? (
                  <div style={{ width: '200px' }}>
                    <SkeletonDisplayText size="large" />
                  </div>
                ) : (
                  <div className="visual-editor-code__version-inner">
                    <p className="visual-editor-code__version-change">
                      {!isEmptyVersion && <span className="changed-spot"></span>}
                      <span>
                        {isEmptyVersion
                          ? i18n.translate('AdminHTML.ShopifyIntegration.CodeEditor.noRecentChange')
                          : i18n.translate('AdminHTML.ShopifyIntegration.CodeEditor.recentChange')}
                      </span>
                    </p>

                    {isEmptyVersion ? (
                      i18n.translate('AdminHTML.ShopifyIntegration.CodeEditor.notHaveVersion')
                    ) : (
                      <Popover
                        active={isShowVersionOptions}
                        preferredAlignment="left"
                        activator={
                          <div className="visual-editor-code__version-dropdown">
                            <Button plain onClick={this.toggleVersionOptions}>
                              <span>{selectedVersionText}</span>
                              {isLoadingVersionContent ? (
                                <Spinner size="small" />
                              ) : (
                                <Icon source={ChevronDownMinor} />
                              )}
                            </Button>
                          </div>
                        }
                        onClose={this.toggleVersionOptions}>
                        <ActionList
                          actionRole="menuitem"
                          items={versions.map((item, index) => {
                            const content = this.getVersionText(item, index);
                            return {
                              content,
                              onAction: () => this.selectVersionCode(item.versionId)
                            };
                          })}
                        />
                      </Popover>
                    )}
                  </div>
                )}

                <Button
                  primary
                  onClick={handleSubmit(this.handleSave)}
                  disabled={
                    !!Object.keys(this.props.synchError).length ||
                    pristine ||
                    submitting ||
                    updateTemplateInstancesLoading
                  }
                  loading={
                    submitting ||
                    (updateTemplateInstancesLoading &&
                      savingWidget?.widgetId === codeEditTemplate?.widgetId)
                  }>
                  {i18n.translate('AdminHTML.Common.Action.save')}
                </Button>
              </div>

              <CodeEditorComponent
                language={'liquid'}
                value={customizeTemplate}
                onChange={value => this.onChangeCode(value)}
                onMount={this.handleEditorDidMount}
              />
            </div>
          </div>
        ) : (
          <div className="visual-editor-code__empty">
            <img src={EditCode} alt="edit-code" />
            <p className="title">
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.editCode_Title')}
            </p>
            <p className="subtitle">
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.editCode_SubTitle')}
            </p>
          </div>
        )}

        <Modal
          open={this.state.isShowModalReset}
          onClose={this.handleHideModalReset}
          title={i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.resetCode_Title')}
          primaryAction={{
            content: i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.discard'),
            onAction: this.handleDiscardChange,
            destructive: true
          }}
          secondaryActions={[
            {
              content: i18n.translate('AdminHTML.Common.Action.cancel'),
              onAction: this.handleHideModalReset
            }
          ]}>
          <Modal.Section>
            <div className="BC-modal-confirm-reset-code">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Developer.resetCode_DescriptionV2',
                {
                  f: (
                    <b>
                      {templatesEditing[this.discardWidgetId]?.sourceLiquid ||
                        templatesEditing[this.discardWidgetId]?.name}
                    </b>
                  )
                }
              )}
            </div>
          </Modal.Section>
        </Modal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`CodeEditorForm`, field, value)),
  resetForm: () => dispatch(reset('CodeEditorForm')),

  updateFieldReducer: (fieldName, data) =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateMultiFields(data)),
  updateFieldTemplateInstancesWidgetsReducer: (fieldName, data) =>
    dispatch(TemplateInstanceAction.handleUpdateField(fieldName, data)),
  updateTemplateInstancesWidgets: (templateId, widgets) =>
    dispatch(
      TemplateInstanceAction.updateTemplateInstancesWidgets(ownProps.i18n, templateId, widgets)
    ),
  getWidgetVersions: (templateId, widgetId) =>
    dispatch(TemplateInstanceAction.getWidgetVersions(ownProps.i18n, templateId, widgetId)),
  getWidgetVersionsContent: (templateId, widgetId, versionId) =>
    dispatch(
      TemplateInstanceAction.getWidgetVersionsContent(
        ownProps.i18n,
        templateId,
        widgetId,
        versionId
      )
    )
});

const mapStateToProps = (state, props) => {
  const instantWidget = _.get(state, 'templateInstance.templateInstance', {});
  const widgetSettings = _.get(state, 'themeIntegration.widgetSettings', {});
  const scssContent = _.get(state, 'themeIntegration.scssContent', '');
  const codeEditTemplate = _.get(state, 'shopifyIntegrationSettings.codeEditTemplate', {});
  const synchError = getFormSyncErrors('CodeEditorForm')(state);

  const isExpandFullscreen =
    _.get(state, 'shopifyIntegrationSettings.device') === DEVICE.FULLSCREEN;

  return {
    initialValues: codeEditTemplate,
    codeEditTemplate,
    codeEditTemplateFormValues: getFormValues('CodeEditorForm')(state) || {},
    synchError,
    instantWidget,
    widgetSettings,
    scssContent,
    isExpandFullscreen,
    templateName: _.get(state, 'templateInstance.templateInstance.templateName', ''),
    customizeTemplate: _.get(state, 'shopifyIntegrationSettings.customizeTemplate', ''),
    templatesEditing: _.get(state, 'shopifyIntegrationSettings.templatesEditing', {}),
    activeMenuItem: _.get(state, 'shopifyIntegrationSettings.activeMenuItem', ''),
    themeName: _.get(state, 'shopifyIntegrationSettings.themeName', ''),
    selectedPage: _.get(state, 'shopifyIntegrationSettings.selectedPage', ''),
    device: _.get(state, 'shopifyIntegrationSettings.device'),
    previewData: _.get(state, 'shopifyIntegrationSettings.previewData', {}),
    themeNameLib: _.get(state, 'templateInstance.templateInstance.themeNameLib', ''),
    themeLibVersion: _.get(state, 'templateInstance.templateInstance.themeLibVersion', ''),
    updateTemplateInstancesLoading: _.get(
      state,
      'templateInstance.updateTemplateInstancesLoading',
      false
    ),
    widgetVersion: _.get(state, 'templateInstance.widgetVersion', {}),
    widgetVersionLoading: _.get(state, 'templateInstance.widgetVersionLoading', false)
  };
};

const CodeEditorForm = reduxForm({
  form: 'CodeEditorForm', // a unique identifier for this form
  enableReinitialize: true
})(CodeEditor);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(CodeEditorForm));
